import { Calendar, CalendarDay, CalendarMonth, WeekDay } from '@app/pcs/models';
import { hasValue, mapIndexed, safeProp } from '@util';
import { localDateFromArray, toISODate } from '@util/date-util';
import * as R from 'ramda';

const februaryLeapYearDaysNumber = 29;
const januaryMonthValue = 1;
const februaryMonthValue = 2;
const weekDaysNumber = 7;
const numberOfMonths = 12;
export const weekdays: WeekDay[] = [
  {name: 'Monday', dayValue: 1},
  {name: 'Tuesday', dayValue: 2},
  {name: 'Wednesday', dayValue: 3},
  {name: 'Thursday', dayValue: 4},
  {name: 'Friday', dayValue: 5},
  {name: 'Saturday', dayValue: 6},
  {name: 'Sunday', dayValue: 7}
];
const monthsOfYear = [
  {name: 'calendar.month.january', monthValue: 1, numberOfDays: 31},
  {name: 'calendar.month.february', monthValue: februaryMonthValue, numberOfDays: 28}, // leap -> 29
  {name: 'calendar.month.march', monthValue: 3, numberOfDays: 31},
  {name: 'calendar.month.april', monthValue: 4, numberOfDays: 30},
  {name: 'calendar.month.may', monthValue: 5, numberOfDays: 31},
  {name: 'calendar.month.june', monthValue: 6, numberOfDays: 30},
  {name: 'calendar.month.july', monthValue: 7, numberOfDays: 31},
  {name: 'calendar.month.august', monthValue: 8, numberOfDays: 31},
  {name: 'calendar.month.september', monthValue: 9, numberOfDays: 30},
  {name: 'calendar.month.october', monthValue: 10, numberOfDays: 31},
  {name: 'calendar.month.november', monthValue: 11, numberOfDays: 30},
  {name: 'calendar.month.december', monthValue: 12, numberOfDays: 31}
];

const getMonthValue = R.prop('monthValue');
const getNumberOfDays: any = R.prop('numberOfDays');
const isLeapYear = ({year}) => new Date(year, 1, 29).getDate() === februaryLeapYearDaysNumber;
const getYear = R.prop('year');

const getDayAt = R.flip(R.nth)(weekdays);
const isFebruary = month => R.equals(getMonthValue(month), februaryMonthValue);
const getMonthAt = (index: number, leapYear: boolean) => {
  const month = R.clone(R.nth(index, monthsOfYear));

  if (isFebruary(month) && leapYear) {
    return R.mergeRight(month, {numberOfDays: februaryLeapYearDaysNumber});
  }

  return month;
};

const initializeMonthsInRange: any = (start, end, leapYear) => {
  const monthsRange = R.range(start, end);
  return R.map(index => getMonthAt(index, leapYear), monthsRange);
};

const initializeMonths = (ttpFirstDate, ttpLastDate) => {
  const firstMonthValue = getMonthValue(ttpFirstDate);
  const lastMonthValue = getMonthValue(ttpLastDate);

  const firstYearMonths = initializeMonthsInRange(firstMonthValue - 1, numberOfMonths, isLeapYear(ttpFirstDate));
  const lastYearMonths = initializeMonthsInRange(0, lastMonthValue, isLeapYear(ttpLastDate));

  return R.concat(firstYearMonths, lastYearMonths);
};

const getFirstDayIndex = (monthIndex, months) => {
  const calculateIndex = (acc, index) => getNumberOfDays(R.nth(index, months)) + acc;
  return R.reduce(calculateIndex, 0, R.range(0, monthIndex));
};

const createDays = (firstTtpDayOfMonth, numberOfDays, totalSelectableDays, selectableDays): CalendarDay[] => {
  const numWeeks = Math.floor(Math.abs((1 - firstTtpDayOfMonth) / weekDaysNumber));
  const firstDayIndex = weekDaysNumber - (firstTtpDayOfMonth - numWeeks * weekDaysNumber) - 1;
  const ttpStartDateIndex = firstTtpDayOfMonth - 1;

  const toDay = index => {
    const nextDayIndex = (firstDayIndex + index + 1) % weekDaysNumber;
    const day = {...getDayAt(nextDayIndex), selectable: false, index};

    if (index >= ttpStartDateIndex && index < (totalSelectableDays + ttpStartDateIndex)) {
      return {...day, ...selectableDays[index - ttpStartDateIndex], index};
    }

    return {...day, out: true};
  };

  return R.map(toDay, R.range(0, numberOfDays));
};

const createPlaceholderDays = lastDayValue => R.map(getDayAt, R.range(0, lastDayValue));

const createCalendarMonth = ({index, firstDayIndex, year, month, days}): CalendarMonth => {
  const {name, monthValue, numberOfDays} = month;
  const startIndex = firstDayIndex;
  const endIndex = firstDayIndex + getNumberOfDays(month);
  const getDays: any = R.slice(startIndex, endIndex);
  const monthDays = getDays(days);
  const firstDayOfMonth = R.head(monthDays);

  return {
    isFirstMonth: index === 0 || monthValue === januaryMonthValue,
    index,
    year,
    name,
    monthValue,
    numberOfDays,
    startIndex,
    endIndex,
    days: monthDays,
    placeholderDays: createPlaceholderDays(firstDayOfMonth.dayValue - 1)
  };
};

export const createWeekdays = (calendarWatermark): WeekDay[] => {
  const weekDays = safeProp('weekDays', calendarWatermark);
  const defaultWeekDays = R.map(R.mergeDeepRight({selected: true}), weekdays);

  return hasValue(weekDays)
    ? mapIndexed((day: WeekDay, index: number) => {
      const selected = R.pathOr(true,['day', 'selected'], weekDays[index]);
      return R.mergeDeepRight({selected}, day);
    }, weekdays) : defaultWeekDays;
};

export const createCalendar = (calendarModel): Calendar => {
  const {totalDays, days: calendarDays, calendarWatermark, ttpId} = calendarModel;

  if (!ttpId && !calendarDays) {
    return {
      id: calendarModel.targetId,
      calendarWatermark
    } as Calendar;
  }

  const ttpFirstDate = localDateFromArray(calendarModel.ttpFirstDate);
  const ttpLastDate = localDateFromArray(calendarModel.ttpLastDate);
  const {dayOfMonth: firstTtpDayOfMonth} = ttpFirstDate;
  const months: any = initializeMonths(ttpFirstDate, ttpLastDate);
  let year = getYear(ttpFirstDate);

  const getNumberOfAllDays: any = R.map(getNumberOfDays, months);
  const numberOfDays = R.reduce(R.add, 0, getNumberOfAllDays);
  const days = createDays(firstTtpDayOfMonth, numberOfDays, totalDays, calendarDays);

  const calendarMonths = <CalendarMonth[]> mapIndexed((month, index) => {
    const firstDayIndex = getFirstDayIndex(index, months);
    if (getMonthValue(month) === januaryMonthValue && index > 0) {
      year = getYear(ttpLastDate);
    }

    return createCalendarMonth({index, firstDayIndex, year, month, days});
  }, months);

  const calendarWeekdays = createWeekdays(calendarWatermark);

  return {
    id: calendarModel.targetId,
    months: calendarMonths,
    numberOfMonths: R.length(months),
    firstDate: toISODate(ttpFirstDate),
    lastDate: toISODate(ttpLastDate),
    weekdays: calendarWeekdays,
    ttpFirstDate: calendarModel.ttpFirstDate,
    ttpLastDate: calendarModel.ttpLastDate,
    validityPeriod: calendarModel.validityPeriod,
    totalDays,
    ttpId,
    calendarWatermark,
    calId: R.prop('id', calendarModel)
  };
};
